import { Headphones, Megaphone, MessagesSquare, Palette, ShieldCheck, Sparkle } from '@/components/ui/icons';
import { useState, type ReactNode } from 'react';
import { APP_NAME } from '@clanfire/shared';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';
import { useGettingStarted } from '@/stores/gettingStarted';

interface Slide {
  icon: ReactNode;
  title: string;
  body: ReactNode;
}

const SLIDES: Slide[] = [
  {
    icon: <Sparkle size={30} />,
    title: `Welcome to ${APP_NAME}!`,
    body: <>Chat, talk and hang out with your people. Here’s a quick tour: it takes about 30 seconds.</>,
  },
  {
    icon: <MessagesSquare size={30} />,
    title: 'Servers and channels',
    body: (
      <>
        Servers are your communities, down the left. Inside each one, <strong># text channels</strong> are for chatting and <strong>🔊 voice channels</strong> are for
        talking. You’re already in <strong>{APP_NAME} Official</strong>, so come and say hi in #general.
      </>
    ),
  },
  {
    icon: <Headphones size={30} />,
    title: 'Hop into voice',
    body: (
      <>
        Click a voice channel to join it. Talk, turn on your camera, share your screen, and play sounds from the <strong>soundboard</strong>. Leave
        whenever you like.
      </>
    ),
  },
  {
    icon: <Palette size={30} />,
    title: 'Make it yours',
    body: (
      <>
        Pick a theme in <strong>Settings → Appearance</strong>, and give your profile a banner, a name style and effects in <strong>Settings → Profile</strong>.
        Send voice messages, GIFs and polls from the message box.
      </>
    ),
  },
  {
    icon: <ShieldCheck size={30} />,
    title: 'Stay safe, and tell us things',
    body: (
      <>
        Turn on <strong>two-factor sign-in</strong> in Settings → Security: it takes a minute. And if something breaks or you have an idea, press{' '}
        <span className="inline-flex translate-y-0.5 items-center gap-1 font-semibold">
          <Megaphone size={14} /> Feedback
        </span>{' '}
        at the top. It goes straight to the team.
      </>
    ),
  },
];

/** A short welcome for new accounts (and anyone who replays it from Settings). */
export function WelcomeTour({ onClose }: { onClose: () => void }) {
  const [index, setIndex] = useState(0);
  const finishTour = useGettingStarted((s) => s.finishTour);
  const slide = SLIDES[index]!;
  const last = index === SLIDES.length - 1;

  const close = () => {
    finishTour();
    onClose();
  };

  return (
    <Modal
      open
      onClose={close}
      title={slide.title}
      size="sm"
      footer={
        <>
          {index === 0 ? (
            <Button variant="ghost" onClick={close}>
              Skip
            </Button>
          ) : (
            <Button variant="ghost" onClick={() => setIndex(index - 1)}>
              Back
            </Button>
          )}
          <Button data-autofocus onClick={() => (last ? close() : setIndex(index + 1))}>
            {last ? 'Let’s go' : 'Next'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col items-center gap-4 text-center" aria-live="polite">
        <div className="flex h-16 w-16 items-center justify-center rounded-3xl bg-accent-soft text-accent">{slide.icon}</div>
        <p className="text-[15px] leading-relaxed text-fg-soft">{slide.body}</p>
        <div className="flex gap-1.5" aria-label={`Step ${index + 1} of ${SLIDES.length}`}>
          {SLIDES.map((s, i) => (
            <button
              key={s.title}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Go to step ${i + 1}`}
              className={cn('h-2 rounded-full transition-all', i === index ? 'w-6 bg-accent' : 'w-2 bg-line-strong hover:bg-fg-muted')}
            />
          ))}
        </div>
      </div>
    </Modal>
  );
}
